/**
 * Hermes — La Plumilla Dorada (búsqueda)
 *
 * Se monta en TODAS las páginas de producto. Pregunta a la app de loyalty si
 * esta visita revela la plumilla; la app conoce el producto del mes (lo elige el
 * equipo de comercio en el admin de la app) y el theme nunca recibe esa lista.
 *
 * Contrato (ver docs/loyalty-architecture.md):
 *   POST /apps/loyalty { _action: 'plumilla_check', producto_id }
 *     -> { success: true, plumilla: false }
 *     -> { success: true, plumilla: true, hallazgo_token: '<firmado, 1 uso>' }
 *
 * Anti-abuso (lo que se puede hacer desde el cliente; el resto vive en la app):
 * - El check NO se dispara en el pageload: exige permanencia (dwell) + una señal
 *   de interacción real. Un scraper que quiera barrer el catálogo tiene que
 *   pagar ese tiempo en cada producto.
 * - Un solo check por producto y por sesión (no se puede recargar para insistir).
 * - La respuesta negativa no dice nada; el token positivo lo firma la app.
 * Nada en este archivo revela dónde está la plumilla.
 */

const API_URL = '/apps/loyalty';
const SEEN_KEY = 'hermesPlumillaChecked';

class PlumillaHunt extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;
    this.rewardUrl = this.dataset.rewardUrl || '/pages/plumilla';
    this.dwell = Number(this.dataset.dwell || 6000);
    this.mock = this.dataset.mock === 'true';

    if (!this.productId) return;
    if (this.alreadyChecked()) return;

    this.armed = false;
    this.timer = null;

    // Arranca el reloj solo cuando hay señal de que un humano está en la página.
    this.arm = this.arm.bind(this);
    window.addEventListener('scroll', this.arm, { once: true, passive: true });
    window.addEventListener('pointermove', this.arm, { once: true, passive: true });
    window.addEventListener('keydown', this.arm, { once: true });
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    window.removeEventListener('scroll', this.arm);
    window.removeEventListener('pointermove', this.arm);
    window.removeEventListener('keydown', this.arm);
  }

  arm() {
    if (this.armed) return;
    this.armed = true;
    this.timer = setTimeout(() => this.check(), this.dwell);
  }

  /** Un check por producto y por sesión. */
  alreadyChecked() {
    try {
      const seen = JSON.parse(sessionStorage.getItem(SEEN_KEY) || '[]');
      return seen.includes(this.productId);
    } catch {
      return false;
    }
  }

  markChecked() {
    try {
      const seen = JSON.parse(sessionStorage.getItem(SEEN_KEY) || '[]');
      seen.push(this.productId);
      sessionStorage.setItem(SEEN_KEY, JSON.stringify(seen.slice(-200)));
    } catch {
      /* sessionStorage lleno o bloqueado: no es fatal */
    }
  }

  async check() {
    // La pestaña en segundo plano no cuenta como visita.
    if (document.visibilityState !== 'visible') return;

    if (this.mock) {
      this.reveal('MOCK-TOKEN');
      return;
    }

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ _action: 'plumilla_check', producto_id: this.productId }),
      });

      if (!response.ok) return;

      const data = await response.json();
      if (data.success && data.plumilla && data.hallazgo_token) {
        // Es la plumilla y sigue SIN reclamarse: la revelamos y NO marcamos el producto
        // como visto. La plumilla del mes no debe "gastarse" por verla — si el usuario la
        // ve y sale, tiene que volver a aparecer al regresar. Solo desaparece cuando
        // alguien la canjea: ahí el backend responde plumilla:false y caemos al else.
        this.reveal(data.hallazgo_token);
      } else {
        // No es la plumilla, o el cupón del mes ya se usó: marcamos el producto como
        // visto para no volver a consultar al backend en cada visita de la sesión.
        this.markChecked();
      }
    } catch {
      // Silencio deliberado: la plumilla es un extra. Si la app no responde, la página de
      // producto sigue funcionando igual, y al no marcar "visto" se reintenta más tarde.
    }
  }

  reveal(token) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'plumilla-hunt__pick';
    button.setAttribute('aria-label', 'Encontraste la Plumilla Dorada. Reclama tu recompensa.');

    // Resplandor dorado, detrás de todo (late suavemente).
    button.appendChild(this.#span('plumilla-hunt__glow'));

    // La burbuja que envuelve la plumilla: se bambolea, y al hacer click estalla.
    button.appendChild(this.#span('plumilla-hunt__bubble'));

    const image = this.dataset.image;
    if (image) {
      const img = document.createElement('img');
      img.src = image;
      img.alt = '';
      img.width = 80;
      img.height = 80;
      button.appendChild(img);
    }

    // Reflejo/vidrio al frente, para que la plumilla se vea DENTRO de la burbuja.
    button.appendChild(this.#span('plumilla-hunt__gloss'));

    // Chispas doradas que titilan DENTRO de la burbuja. Decorativas: el contenedor
    // .plumilla-hunt tiene pointer-events:none y solo el botón es clickeable, así que NO
    // se comen ningún clic (a diferencia del canvas de la versión vieja).
    const sparkles = this.#span('plumilla-hunt__sparkles');
    const TOTAL = 20;
    for (let i = 0; i < TOTAL; i++) {
      const chispa = document.createElement('i');
      const angulo = (360 / TOTAL) * i + (Math.random() * 22 - 11);
      const distancia = 22 + Math.random() * 26; // dentro de la burbuja
      const retraso = Math.random() * 2.8;
      chispa.style.setProperty('--a', `${angulo}deg`);
      chispa.style.setProperty('--d', `${distancia}px`);
      chispa.style.setProperty('--delay', `${retraso}s`);
      sparkles.appendChild(chispa);
    }
    button.appendChild(sparkles);

    // Posición aleatoria, lejos de los bordes y de la zona del botón de compra.
    button.style.setProperty('--plumilla-x', `${10 + Math.random() * 70}vw`);
    button.style.setProperty('--plumilla-y', `${15 + Math.random() * 55}vh`);

    button.addEventListener('click', () => this.#pop(button, token));

    this.appendChild(button);
    this.dataset.state = 'found';
  }

  /** Al hacer click: la burbuja estalla en gotitas, la plumilla queda un instante, y
   *  luego se redirige a la página del cupón. */
  #pop(button, token) {
    if (button.dataset.popping) return;
    button.dataset.popping = '1';

    const go = () => {
      window.location.href = `${this.rewardUrl}?h=${encodeURIComponent(token)}`;
    };

    // Con reduced-motion, sin espectáculo: directo al cupón.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      go();
      return;
    }

    button.classList.add('is-popping');

    // Gotitas que salen disparadas hacia afuera (la burbuja reventando).
    const burst = this.#span('plumilla-hunt__burst');
    const TOTAL = 18;
    for (let i = 0; i < TOTAL; i++) {
      const gota = document.createElement('i');
      const angulo = (360 / TOTAL) * i + (Math.random() * 16 - 8);
      const distancia = 60 + Math.random() * 55;
      gota.style.setProperty('--a', `${angulo}deg`);
      gota.style.setProperty('--d', `${distancia}px`);
      gota.style.setProperty('--delay', `${Math.random() * 0.06}s`);
      burst.appendChild(gota);
    }
    button.appendChild(burst);

    // Deja ver la plumilla ya "libre" un momento y redirige.
    setTimeout(go, 680);
  }

  /** Crea un <span> decorativo (aria-hidden) con la clase dada. */
  #span(className) {
    const el = document.createElement('span');
    el.className = className;
    el.setAttribute('aria-hidden', 'true');
    return el;
  }
}

if (!customElements.get('plumilla-hunt')) {
  customElements.define('plumilla-hunt', PlumillaHunt);
}
